import type {
	DeadlineUrgency,
	InfractionOrigin,
	InfractionStatus,
} from "../types/infraction.types";
import { INFRACTION_STATUS } from "./infraction-status.constant";

/** `InfractionStatus` conforme backend (`NotificationReceived` = 1 … `Warning` = 9). */
export const INFRACTION_API_STATUS = {
	NOTIFICATION_RECEIVED: 1,
	AWAITING_DRIVER_INDICATION: 2,
	DRIVER_INDICATED: 3,
	IN_DEFENSE: 4,
	PENDING_PAYMENT: 5,
	PAID: 6,
	EXPIRED: 7,
	CANCELLED: 8,
	WARNING: 9,
} as const;

export type InfractionApiStatusCode =
	(typeof INFRACTION_API_STATUS)[keyof typeof INFRACTION_API_STATUS];

/** `InfractionDisplayStatus` — cenário operacional calculado para a listagem. */
export const INFRACTION_DISPLAY_STATUS = {
	NOTIFICATION_RECEIVED: 1,
	AWAITING_DRIVER_INDICATION: 2,
	DRIVER_INDICATION_EXPIRED: 3,
	PENDING_PAYMENT: 4,
	PAYMENT_EXPIRED: 5,
	DRIVER_ASSIGNED: 6,
	PAID: 7,
	CANCELLED: 8,
} as const;

export const DISPLAY_STATUS_TO_INFRACTION_STATUS: Record<
	number,
	InfractionStatus
> = {
	[INFRACTION_DISPLAY_STATUS.NOTIFICATION_RECEIVED]:
		INFRACTION_STATUS.NOTIFICATION_RECEIVED,
	[INFRACTION_DISPLAY_STATUS.AWAITING_DRIVER_INDICATION]:
		INFRACTION_STATUS.AWAITING_DRIVER_INDICATION,
	[INFRACTION_DISPLAY_STATUS.DRIVER_INDICATION_EXPIRED]:
		INFRACTION_STATUS.EXPIRED,
	[INFRACTION_DISPLAY_STATUS.PENDING_PAYMENT]:
		INFRACTION_STATUS.PAYMENT_PENDING,
	[INFRACTION_DISPLAY_STATUS.PAYMENT_EXPIRED]:
		INFRACTION_STATUS.PAYMENT_EXPIRED,
	[INFRACTION_DISPLAY_STATUS.DRIVER_ASSIGNED]:
		INFRACTION_STATUS.DRIVER_INDICATED,
	[INFRACTION_DISPLAY_STATUS.PAID]: INFRACTION_STATUS.PAID,
	[INFRACTION_DISPLAY_STATUS.CANCELLED]: INFRACTION_STATUS.CANCELLED,
};

export const API_STATUS_TO_INFRACTION_STATUS: Record<number, InfractionStatus> =
	{
		[INFRACTION_API_STATUS.NOTIFICATION_RECEIVED]:
			INFRACTION_STATUS.NOTIFICATION_RECEIVED,
		[INFRACTION_API_STATUS.AWAITING_DRIVER_INDICATION]:
			INFRACTION_STATUS.AWAITING_DRIVER_INDICATION,
		[INFRACTION_API_STATUS.DRIVER_INDICATED]:
			INFRACTION_STATUS.DRIVER_INDICATED,
		[INFRACTION_API_STATUS.IN_DEFENSE]: INFRACTION_STATUS.IN_DEFENSE,
		[INFRACTION_API_STATUS.PENDING_PAYMENT]: INFRACTION_STATUS.PAYMENT_PENDING,
		[INFRACTION_API_STATUS.PAID]: INFRACTION_STATUS.PAID,
		[INFRACTION_API_STATUS.EXPIRED]: INFRACTION_STATUS.EXPIRED,
		[INFRACTION_API_STATUS.CANCELLED]: INFRACTION_STATUS.CANCELLED,
		[INFRACTION_API_STATUS.WARNING]: INFRACTION_STATUS.WARNING,
	};

/** `InfractionOrigin` conforme swagger. */
export const INFRACTION_API_ORIGIN = {
	MANUAL: 1,
	HABILITAR_ME: 2,
	/** @deprecated Use `HABILITAR_ME`. */
	INTEGRATED: 2,
} as const;

export const API_ORIGIN_TO_DOMAIN_ORIGIN: Record<number, InfractionOrigin> = {
	[INFRACTION_API_ORIGIN.MANUAL]: "manual",
	[INFRACTION_API_ORIGIN.HABILITAR_ME]: "integrated",
};

/** `InfractionOffenderType` — condutor responsável pela indicação (swagger: 3 = Condutor). */
export const INFRACTION_OFFENDER_TYPE = {
	DRIVER: 3,
} as const;

/** `InfractionDeadlineType` conforme swagger. */
export const INFRACTION_DEADLINE_TYPE = {
	NONE: 0,
	DRIVER_INDICATION: 1,
	PAYMENT: 2,
	PAID: 3,
	CANCELLED: 4,
} as const;

/** `InfractionDeadlineSituation` conforme swagger. */
export const INFRACTION_DEADLINE_SITUATION = {
	NONE: 0,
	EXPIRED: 1,
	DUE_TODAY: 2,
	UPCOMING: 3,
	COMPLETED: 4,
	CANCELLED: 5,
} as const;

export const DEADLINE_SITUATION_TO_URGENCY: Record<number, DeadlineUrgency> = {
	[INFRACTION_DEADLINE_SITUATION.NONE]: "normal",
	[INFRACTION_DEADLINE_SITUATION.EXPIRED]: "expired",
	[INFRACTION_DEADLINE_SITUATION.DUE_TODAY]: "critical",
	[INFRACTION_DEADLINE_SITUATION.UPCOMING]: "warning",
	[INFRACTION_DEADLINE_SITUATION.COMPLETED]: "paid",
	[INFRACTION_DEADLINE_SITUATION.CANCELLED]: "cancelled",
};
